import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Label
} from "recharts";
import {
  Target,
  Loader2,
  RefreshCw,
  Zap,
  TrendingUp,
  Users,
  AlertTriangle,
  Crown,
  Crosshair,
  Gem,
  UserMinus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUADRANT_CONFIG = {
  leader: { 
    label: "Leaders", 
    color: "#10b981", 
    bgColor: "bg-emerald-500/10",
    textColor: "text-emerald-500",
    icon: Crown,
    description: "High market presence & high innovation"
  },
  challenger: { 
    label: "Challengers", 
    color: "#f59e0b", 
    bgColor: "bg-amber-500/10",
    textColor: "text-amber-500",
    icon: TrendingUp,
    description: "Low market presence but high innovation"
  },
  niche: { 
    label: "Niche Players", 
    color: "#8b5cf6", 
    bgColor: "bg-purple-500/10",
    textColor: "text-purple-500",
    icon: Gem,
    description: "High market presence but low innovation"
  },
  follower: { 
    label: "Followers", 
    color: "#6b7280", 
    bgColor: "bg-gray-500/10",
    textColor: "text-gray-500",
    icon: UserMinus,
    description: "Low market presence & low innovation"
  }
};

const CustomTooltip = ({ active, payload, darkMode }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const quadrantConfig = QUADRANT_CONFIG[data.quadrant];
    
    return (
      <div className={`p-4 rounded-xl shadow-xl border ${
        darkMode ? 'bg-[#161b22] border-[#30363d]' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: quadrantConfig?.color }} />
          <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {data.competitor_name}
          </p>
        </div>
        <div className="space-y-1 text-sm">
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Market Presence: <span className="font-medium">{data.x_axis}%</span>
          </p>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Innovation Score: <span className="font-medium">{data.y_axis}%</span>
          </p>
          <Badge className={`mt-2 ${quadrantConfig?.bgColor} ${quadrantConfig?.textColor} border-0`}>
            {quadrantConfig?.label}
          </Badge>
          {data.competitive_threat && (
            <p className={`mt-1 ${
              data.competitive_threat === 'high' ? 'text-red-500' :
              data.competitive_threat === 'medium' ? 'text-amber-500' :
              'text-emerald-500'
            }`}>
              Threat Level: {data.competitive_threat}
            </p>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default function MarketPosition() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [positions, setPositions] = useState([]);
  const [quadrants, setQuadrants] = useState({});
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedQuadrant, setSelectedQuadrant] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/advanced/market-position/${brand}/${region}`);
      setPositions(res.data.positions || []);
      setQuadrants(res.data.quadrants || {});
      if (res.data.market_insights) {
        setInsights(res.data.market_insights);
      }
    } catch (error) {
      console.error("Error fetching market positions:", error);
    } finally {
      setLoading(false);
    }
  }, [brand, region]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/advanced/market-position/analyze/${brand}/${region}`);
      toast.success(`Analyzed ${res.data.analyzed || 0} competitors!`);
      setInsights(res.data.market_insights);
      fetchData();
    } catch (error) {
      toast.error("Failed to analyze market positions");
    } finally {
      setAnalyzing(false);
    }
  };

  const filteredPositions = selectedQuadrant 
    ? positions.filter(p => p.quadrant === selectedQuadrant)
    : positions;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="market-position-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="market-position-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Market Positioning Matrix
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Visualize competitor positions across market presence and innovation axes
          </p>
        </div>
        <Button
          onClick={runAnalysis}
          disabled={analyzing}
          className="bg-[#DC143C] hover:bg-[#b91031]"
          data-testid="analyze-positions-btn"
        >
          {analyzing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Zap className="w-4 h-4 mr-2" />
          )}
          AI Analysis
        </Button>
      </div>

      {/* Quadrant Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(QUADRANT_CONFIG).map(([key, config]) => {
          const count = quadrants[key === "leader" ? "leaders" : key === "challenger" ? "challengers" : key]?.length || 0;
          const IconComponent = config.icon;
          const isSelected = selectedQuadrant === key;
          
          return (
            <Card 
              key={key}
              className={`cursor-pointer transition-all ${
                darkMode ? 'bg-[#161b22] border-[#30363d]' : ''
              } ${isSelected ? `ring-2 ring-offset-2 ${darkMode ? 'ring-offset-[#0d1117]' : 'ring-offset-white'}` : ''}`}
              style={{ borderColor: isSelected ? config.color : undefined }}
              onClick={() => setSelectedQuadrant(isSelected ? null : key)}
              data-testid={`quadrant-${key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className={`p-2 rounded-lg ${config.bgColor}`}>
                    <IconComponent className={`w-5 h-5 ${config.textColor}`} />
                  </div>
                  <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {count}
                  </span>
                </div>
                <p className={`text-sm font-medium mt-2 ${config.textColor}`}>
                  {config.label}
                </p>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {config.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main Chart */}
        <div className="col-span-8">
          <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : ''}>
                <Crosshair className="w-5 h-5 inline mr-2" />
                Competitive Landscape
              </CardTitle>
              <CardDescription>
                {selectedQuadrant 
                  ? `Showing ${QUADRANT_CONFIG[selectedQuadrant].label} (${filteredPositions.length})` 
                  : `Showing all competitors (${positions.length})`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 40 }}>
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke={darkMode ? '#30363d' : '#e5e7eb'}
                    />
                    <XAxis 
                      type="number" 
                      dataKey="x_axis" 
                      name="Market Presence"
                      domain={[0, 100]}
                      tick={{ fill: darkMode ? '#8b949e' : '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: darkMode ? '#30363d' : '#e5e7eb' }}
                    >
                      <Label 
                        value="Market Presence" 
                        position="bottom" 
                        offset={20}
                        style={{ fill: darkMode ? '#8b949e' : '#6b7280', fontSize: 14 }}
                      />
                    </XAxis>
                    <YAxis 
                      type="number" 
                      dataKey="y_axis" 
                      name="Innovation"
                      domain={[0, 100]}
                      tick={{ fill: darkMode ? '#8b949e' : '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: darkMode ? '#30363d' : '#e5e7eb' }}
                    >
                      <Label 
                        value="Innovation Score" 
                        angle={-90} 
                        position="left" 
                        offset={20}
                        style={{ fill: darkMode ? '#8b949e' : '#6b7280', fontSize: 14, textAnchor: 'middle' }}
                      />
                    </YAxis>
                    
                    {/* Quadrant dividers */}
                    <ReferenceLine 
                      x={50} 
                      stroke={darkMode ? '#30363d' : '#d1d5db'} 
                      strokeDasharray="5 5" 
                    />
                    <ReferenceLine 
                      y={50} 
                      stroke={darkMode ? '#30363d' : '#d1d5db'} 
                      strokeDasharray="5 5" 
                    />
                    
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    
                    <Scatter name="Competitors" data={filteredPositions}>
                      {filteredPositions.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`}
                          fill={QUADRANT_CONFIG[entry.quadrant]?.color || '#6b7280'}
                          fillOpacity={0.8}
                          r={entry.size || 10}
                        />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-dashed" style={{ borderColor: darkMode ? '#30363d' : '#e5e7eb' }}>
                {Object.entries(QUADRANT_CONFIG).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: config.color }}
                    />
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {config.label}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Competitor List Sidebar */}
        <div className="col-span-4 space-y-4">
          {/* Market Insights */}
          {insights && (
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>
                  <AlertTriangle className="w-4 h-4 inline mr-2 text-amber-500" />
                  Market Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {insights}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Competitor Rankings */}
          <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>
                <Users className="w-4 h-4 inline mr-2" />
                {selectedQuadrant ? QUADRANT_CONFIG[selectedQuadrant].label : 'All Competitors'}
              </CardTitle>
              <CardDescription>
                Sorted by market presence
              </CardDescription>
            </CardHeader>
            <CardContent className="max-h-[400px] overflow-y-auto">
              <div className="space-y-2">
                {filteredPositions
                  .sort((a, b) => b.x_axis - a.x_axis)
                  .map((pos, idx) => {
                    const config = QUADRANT_CONFIG[pos.quadrant];
                    return (
                      <div 
                        key={pos.competitor_id || idx}
                        className={`p-3 rounded-lg ${darkMode ? 'bg-[#21262d] hover:bg-[#30363d]' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-[#DC143C] text-white' :
                              idx === 1 ? 'bg-amber-500 text-white' :
                              idx === 2 ? 'bg-emerald-500 text-white' :
                              darkMode ? 'bg-[#30363d] text-gray-400' : 'bg-gray-200 text-gray-500'
                            }`}>
                              {idx + 1}
                            </span>
                            <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {pos.competitor_name}
                            </span>
                          </div>
                          <Badge className={`${config?.bgColor} ${config?.textColor} border-0 text-xs`}>
                            {config?.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs">
                          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                            Presence: {pos.x_axis}%
                          </span>
                          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                            Innovation: {pos.y_axis}%
                          </span>
                        </div>
                        {pos.key_strengths && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {pos.key_strengths.slice(0, 2).map((s) => (
                              <Badge key={s} className="bg-blue-500/10 text-blue-500 border-0 text-xs">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                {filteredPositions.length === 0 && (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No competitors in this quadrant</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
